import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, ChevronRight, Menu, X, Search, ChevronDown } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { usePermission } from '../context/PermissionContext';

const HelpPage = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { hasPermission, hasAnyResource } = usePermission();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedChapters, setExpandedChapters] = useState({});

  const hasHelpAccess = hasAnyResource(['help']);

  useEffect(() => {
    setLoading(true);
    fetch('/api/help/chapters')
      .then(r => r.json())
      .then(r => {
        if (r.status === 'success') {
          setChapters(r.data);
          if (r.data.length > 0) {
            setActiveChapter(r.data[0]);
            if (r.data[0].sections.length > 0) {
              setActiveSection(r.data[0].sections[0]);
            }
            const expanded = {};
            r.data.forEach(ch => { expanded[ch.chapter] = true; });
            setExpandedChapters(expanded);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSectionClick = useCallback((chapter, section) => {
    setActiveChapter(chapter);
    setActiveSection(section);
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, []);

  const toggleChapter = useCallback((chapterNum) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterNum]: !prev[chapterNum]
    }));
  }, []);

  const filteredChapters = searchQuery
    ? chapters.map(ch => ({
        ...ch,
        sections: ch.sections.filter(s =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(ch => ch.sections.length > 0)
    : chapters;

  if (!hasHelpAccess) {
    return (
      <div className="space-y-6 p-6">
        <div className={`p-8 rounded-2xl border shadow-sm text-center ${theme === 'light' ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-800'}`}>
          <Book size={48} className="mx-auto mb-4 text-slate-400" />
          <h2 className="text-xl font-semibold mb-2">
            {t('help.no_permission') || '暂无查看帮助的权限'}
          </h2>
          <p className="text-slate-500">
            {t('help.contact_admin') || '请联系管理员为你分配帮助模块的查看权限'}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className={`p-8 rounded-2xl border shadow-sm ${theme === 'light' ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-800'}`}>
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  const isDark = theme === 'dark';

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col p-6">
      <div className={`flex-1 flex overflow-hidden rounded-2xl border shadow-sm ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
        {/* Sidebar */}
        <div className={`
          ${sidebarOpen ? 'w-72' : 'w-0 md:w-0'}
          ${sidebarOpen ? 'opacity-100' : 'opacity-0 md:hidden'}
          transition-all duration-300 ease-in-out overflow-hidden
          ${isDark ? 'border-r border-slate-800' : 'border-r border-slate-200'}
          flex flex-col shrink-0
        `}>
          <div className={`p-4 border-b ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Book size={18} className="text-blue-600" />
                <span className="font-semibold">{t('help.title') || '帮助文档'}</span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className={`p-1 rounded-md ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
              >
                <X size={16} />
              </button>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('help.search') || '搜索文档...'}
                className={`w-full pl-8 pr-3 py-2 text-sm rounded-lg border outline-none ${
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500'
                    : 'bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400'
                } focus:ring-2 focus:ring-blue-500/40`}
              />
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto p-2 space-y-1">
            {filteredChapters.map(ch => (
              <div key={ch.chapter}>
                <button
                  onClick={() => toggleChapter(ch.chapter)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg ${
                    activeChapter?.chapter === ch.chapter
                      ? isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-50 text-blue-700'
                      : isDark ? 'hover:bg-slate-800 text-slate-300' : 'hover:bg-slate-100 text-slate-600'
                  }`}
                >
                  <ChevronDown size={14} className={`transition-transform ${expandedChapters[ch.chapter] ? '' : '-rotate-90'}`} />
                  <span className="truncate">{t('help.chapter') || '第'} {ch.chapter}{t('help.chapter_suffix') || '章'}：{ch.title}</span>
                </button>
                {expandedChapters[ch.chapter] && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {ch.sections.map(s => (
                      <button
                        key={s.id}
                        onClick={() => handleSectionClick(ch, s)}
                        className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${
                          activeSection?.id === s.id
                            ? isDark ? 'bg-slate-800 text-blue-400' : 'bg-slate-100 text-blue-600'
                            : isDark ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {s.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top bar (mobile) */}
          <div className={`flex items-center gap-3 p-3 border-b ${isDark ? 'border-slate-800' : 'border-slate-200'} md:hidden`}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-1.5 rounded-md ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
            >
              <Menu size={18} />
            </button>
            <span className="text-sm font-medium truncate">
              {activeChapter && `${t('help.chapter') || '第'}${activeChapter.chapter}${t('help.chapter_suffix') || '章'}`}
            </span>
          </div>
          
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-6 md:p-10">
              {activeSection ? (
                <>
                  {/* Breadcrumb */}
                  <div className={`flex items-center gap-1.5 text-xs mb-6 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    <span>{activeChapter?.title}</span>
                    <ChevronRight size={12} />
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>{activeSection.title}</span>
                  </div>

                  {/* Title */}
                  <h1 className={`text-2xl md:text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {activeChapter && `${t('help.chapter') || '第'}${activeChapter.chapter}${t('help.chapter_suffix') || '章'}：${activeChapter.title} — ${activeSection.title}`}
                  </h1>

                  {/* HTML Content */}
                  <div 
                    className={`help-content help-content-${theme}`}
                    dangerouslySetInnerHTML={{ __html: activeSection.content }}
                  />
                </>
              ) : (
                <div className={`text-center py-20 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  <Book size={48} className="mx-auto mb-4 opacity-40" />
                  <p>{t('help.select_topic') || '请从左侧选择一个主题'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
